import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { z } from 'zod';
import { UserEntity } from '@nohara/oidc-core';

import { useUpdateUser } from '../api';

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: UserEntity;
}

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, { message: 'users.form.name.required' }),
});

type FormValues = z.infer<typeof formSchema>;

export const EditUserDialog = ({ open, onClose, user }: EditUserDialogProps) => {
  const { t } = useTranslation();
  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: user?.name || '',
    },
  });

  // Update form values when initialName changes
  useMemo(() => {
    if (user) {
      form.reset({
        name: user.name,
      });
    }
  }, [form, user]);

  const handleSubmit = async (data: FormValues) => {
    try {
      await updateUser({ userId: user?.id, data });
      onClose();
    } catch {
      // Error is handled by the mutation
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      aria-labelledby='edit-user-dialog-title'
    >
      <DialogTitle id='edit-user-dialog-title'>{t('users.edit.title')}</DialogTitle>
      <DialogContent>
        <FormContainer formContext={form} onSuccess={handleSubmit}>
          <Box mb={3} pt={1}>
            <TextFieldElement
              name='name'
              label={t('users.form.name.label')}
              placeholder={t('users.form.name.placeholder')}
              required
              disabled={isUpdating}
              fullWidth
              helperText={t('users.form.name.helper')}
            />
          </Box>

          <Box display='flex' justifyContent='flex-end'>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              {t('common.cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              disabled={isUpdating || !form.formState.isValid}
              startIcon={isUpdating ? <CircularProgress size={20} /> : null}
            >
              {isUpdating ? t('common.submitting') : t('common.save')}
            </Button>
          </Box>
        </FormContainer>
      </DialogContent>
    </Dialog>
  );
};
